import { faImage } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    CorporationResponse,
    FileParameter,
} from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { FileUploadButton } from 'components/button/FileUploadButton';
import { AuthorizedImage } from 'components/image/AuthorizedImage';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { onUploadInputChange } from 'pages/Settings/Corporations/shared';
import { useFileUpload } from 'pages/Settings/FileLibrary/useFileUpload';
import { useRef } from 'react';
import styled from 'styled-components';
import { KCFColor } from 'styles/themes/KCFColor';

const UploadWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 1em;
`;

const CorporationIcon = styled(AuthorizedImage)`
    height: auto;
    max-height: 8em;
    max-width: 8em;
    margin-bottom: 0.5em;
`;

export type CorporationIconDetailsProps = Readonly<{
    corporation: CorporationResponse;
    onUpload: () => void;
}>;

export function CorporationIconDetails({
    corporation,
    onUpload,
}: CorporationIconDetailsProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { showToast } = useNotificationContext();
    const {
        mutate: uploadFile,
        isError: isFileError,
        error: fileError,
        isPending: isUploading,
    } = useFileUpload(
        {
            corporation,
        },
        {
            onSuccess: (data) => {
                showToast({
                    type: 'success',
                    content: `${data.DisplayName} successfully uploaded.`,
                });
            },
            onError: (_e, data) => {
                showToast({
                    type: 'error',
                    content: `An error occurred while uploading file: ${data.name}.`,
                });
            },
        }
    );

    const {
        mutate: uploadIcon,
        isPending: isSavingLogo,
        error: iconError,
        isError: isIconError,
    } = useMutation({
        mutationFn: (fileParameter: FileParameter) =>
            sdClient.uploadCorporationIcon({ id: corporation.Id, file: fileParameter }),

        onSuccess: () => {
            onUpload();
            showToast({
                type: 'success',
                content: 'Logo successfully changed.',
            });
        },

        onError: (_e) => {
            showToast({
                type: 'error',
                content: 'An error occurred while setting logo.',
            });
        },
    });

    return (
        <UploadWrapper>
            <CorporationIcon
                src={corporation.IconUrl}
                aria-label={`Logo for ${corporation.Name}`}
                alt={`Logo for ${corporation.Name}`}
                fallback={
                    <FontAwesomeIcon
                        icon={faImage}
                        color={KCFColor.mediumGrey5}
                        title="Corporation Icon"
                        aria-label="Corporation Icon"
                        size="5x"
                    />
                }
            />
            <FileUploadButton
                fileInputRef={fileInputRef}
                onUpload={() =>
                    onUploadInputChange(fileInputRef.current, uploadIcon, uploadFile)
                }
                onButtonClick={() => fileInputRef.current?.click()}
                error={fileError || iconError}
                isError={isIconError || isFileError}
                isUploading={isUploading || isSavingLogo}
                buttonLabel={corporation.IconUrl ? 'Change Logo' : 'Upload Logo'}
            />
        </UploadWrapper>
    );
}
